/**
 * Human-readable appointment service type for headers (e.g. "Individual Therapy").
 *
 * Source of truth: Unify therapy-service-type coding on any Appointment.serviceType entry,
 * then therapy/session display text on those entries. Appointment.appointmentType is modality
 * (telehealth / in-person) and must not drive service-type display.
 */
import type { Appointment } from '@medplum/fhirtypes';
import { THERAPY_SERVICE_TYPE_SYSTEM } from '../../lib/caseloadFhirConstants';
import { therapyServiceTypeLabel } from '../../lib/fhirValueSets/sessionAndEpisodeTypes';

/** Prefer Unify `therapy-service-type` coding when present (stable vs display text alone). */
function unifyTherapyServiceTypeCode(appointment: Appointment): string | undefined {
  for (const serviceType of appointment.serviceType ?? []) {
    for (const c of serviceType.coding ?? []) {
      if (c.system === THERAPY_SERVICE_TYPE_SYSTEM && c.code) {
        return c.code;
      }
    }
  }
  return undefined;
}

function getServiceTypeDisplayText(appointment: Appointment): string {
  for (const svc of appointment.serviceType ?? []) {
    if (svc?.text?.trim()) return svc.text;
    const display = svc?.coding?.find((c) => c.display?.trim())?.display;
    if (display?.trim()) return display;
  }
  return '';
}

/**
 * Human-readable appointment service type for headers (e.g. "Individual Therapy").
 * Prefers FHIR display/text, then therapy-service-type catalog labels.
 */
export function getAppointmentServiceTypeLabel(
  appointment: Appointment | null | undefined,
): string | undefined {
  if (!appointment) return undefined;
  const display = getServiceTypeDisplayText(appointment).trim();
  if (display) return display;

  const unify = unifyTherapyServiceTypeCode(appointment);
  if (unify) {
    return therapyServiceTypeLabel(unify);
  }

  return undefined;
}
